import { useState, useRef, useEffect } from "react";
import { useCommunityAuth } from "../../contexts/CommunityAuthContext";
import { Formik, Form, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faShieldAlt,
  faClipboardList,
  faCheck,
  faArrowRight,
  faArrowLeft,
  faBug,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { InlineSpinner } from "../../componentes/LoadingIndicator";
import axios from "axios";
import secureApi from "../../utils/api";
import toast, { Toaster } from "react-hot-toast";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import FormField from "./FormField";
import ReCaptcha from "../../componentes/ReCaptcha";
import SuccessNotification from "../../componentes/SuccessNotification";
import UploadProgress from "../../componentes/UploadProgress";
import FileSizeHelper from "../../componentes/FileSizeHelper";
import { malwareValidationSchema } from "./validationSchemas";
import {
  malwareTypes,
  detectionMethods,
  infectionVectors,
  platformTypes,
  reporterTypes,
} from "./formConfig";
import { sanitizeFormData } from "../../utils/security";
import { handleSubmissionError } from "../../utils/errorHandling";
import { handleUploadError, validateFileSizes } from "../../utils/uploadErrorHandler";
import { API_BASE_URL } from '../../api/config';

const baseUrl = API_BASE_URL;

const ScrollToError = () => {
  const { submitCount, errors } = useFormikContext();
  const prevSubmitCount = useRef(submitCount);

  useEffect(() => {
    if (
      submitCount > prevSubmitCount.current &&
      Object.keys(errors).length > 0
    ) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
        element.focus();
      }
    }
    prevSubmitCount.current = submitCount;
  }, [submitCount, errors]);

  return null;
};

const SteppedMalwareForm = () => {
  const { user } = useCommunityAuth();
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaRef, setRecaptchaRef] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [submittedTrackingId, setSubmittedTrackingId] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  const steps = [
    {
      id: "reporter",
      title: t("incident report.malware.steps.reporter_info"),
      icon: faUser,
      fields: ["full_name", "email", "phone_number", "organization", "reporter_type"]
    },
    {
      id: "malware",
      title: t("incident report.malware.steps.malware_details"),
      icon: faShieldAlt,
      fields: ["malware_type", "detection_method", "affected_platform", "first_detection_date", "infection_vector"]
    },
    {
      id: "analysis",
      title: t("incident report.malware.steps.sample_analysis"),
      icon: faClipboardList,
      fields: ["observed_behavior", "impact_assessment", "malware_sample", "additional_files"]
    },
    {
      id: "verify",
      title: "Email Verification",
      icon: faCheck,
      fields: ["email_otp"]
    }
  ];

  const initialValues = {
    full_name: user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : "",
    email: user?.email || "",
    phone_number: user?.phone || "",
    organization: "",
    reporter_type: "",
    malware_type: "",
    other_malware_type: "",
    detection_method: "",
    other_detection_method: "",
    affected_platform: "",
    first_detection_date: "",
    infection_vector: "",
    other_infection_vector: "",

    observed_behavior: "",
    impact_assessment: "",
    malware_sample: [],
    additional_files: [],
    // Archive password fields for password-protected ZIPs
    has_archive_password: false,
    archive_password: "",

    recaptcha: "",
    email_otp: "",
    email_for_otp: "",
  };

  const handleNext = async (validateForm, values, setTouched) => {
    if (currentStep < steps.length - 1) {
      // Get current step fields
      const currentStepFields = steps[currentStep].fields;

      // Create touched object for current step fields
      const touchedFields = {};
      currentStepFields.forEach(field => {
        touchedFields[field] = true;
        // Handle conditional fields
        if (field === 'other_malware_type' && values.malware_type === 'other') {
          touchedFields['other_malware_type'] = true;
        }
        if (field === 'other_detection_method' && values.detection_method === 'other') {
          touchedFields['other_detection_method'] = true;
        }
        if (field === 'other_infection_vector' && values.infection_vector === 'other') {
          touchedFields['other_infection_vector'] = true;
        }
      });

      // Set touched for current step fields
      setTouched(touchedFields);

      // Validate the form
      const errors = await validateForm();

      // Check if current step has any validation errors
      const currentStepErrors = currentStepFields.some(field => {
        if (field === 'other_malware_type' && values.malware_type !== 'other') {
          return false; // Skip validation if not needed
        }
        if (field === 'other_detection_method' && values.detection_method !== 'other') {
          return false; // Skip validation if not needed
        }
        if (field === 'other_infection_vector' && values.infection_vector !== 'other') {
          return false; // Skip validation if not needed
        }
        return errors[field];
      });

      if (!currentStepErrors) {
        setCurrentStep(currentStep + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        // Scroll to first error field
        const firstErrorField = currentStepFields.find(field => errors[field]);
        if (firstErrorField) {
          const element = document.querySelector(`[name="${firstErrorField}"]`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
            element.focus();
          }
        }
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (
    values,
    { resetForm, setErrors, setSubmitting }
  ) => {
    try {
      setIsSubmitting(true);
      setUploadError(null);
      setUploadProgress(0);

      if (!values.recaptcha) {
        toast.error(t("incident report.ui.recaptcha_error"));
        return;
      }

      // Pre-validate file sizes
      const fileSizeErrors = validateFileSizes({
        malware_sample: values.malware_sample,
        additional_files: values.additional_files
      });

      if (fileSizeErrors.length > 0) {
        const errorMessage = fileSizeErrors.map(err => err.message).join('\n');
        setUploadError(errorMessage);
        toast.error('Please reduce file sizes before uploading');
        return;
      }

      const { sanitized, issues } = sanitizeFormData(values);

      if (issues.length > 0) {
        toast.error("Security issues detected in form data");
        setErrors(
          issues.reduce(
            (acc, issue) => ({
              ...acc,
              [issue.field]: `Security issue: ${issue.issues.join(", ")}`,
            }),
            {}
          )
        );
        return;
      }

      const formData = new FormData();
      Object.entries(sanitized).forEach(([key, value]) => {
        if (key === "malware_sample" && value) {
          Array.from(value).forEach((file) => formData.append("malware_samples", file));
        } else if (key === "additional_files" && value) {
          Array.from(value).forEach((file) => formData.append("additional_files", file));
        } else if (value !== null && value !== undefined && value !== "") {
          formData.append(key, value);
        }
      });

      // Get CSRF token from cookie
      const csrfToken = document.cookie
        .split('; ')
        .find(row => row.startsWith('csrftoken='))
        ?.split('=')[1];

      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          ...(csrfToken && { "X-CSRFToken": csrfToken })
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      };

      const response = await axios.post(
        `${baseUrl}/api/incident-reports/malware/create/`,
        formData,
        config
      );

      if (response.data.status === "success") {
        setSubmittedEmail(values.email);
        setSubmittedTrackingId(response.data.tracking_id || 'Not available');
        setShowSuccess(true);
        resetForm({ values: { ...initialValues, malware_sample: null, additional_files: null, recaptcha: "" } });
        const fileInputs = ["malware_sample", "additional_files"];
        fileInputs.forEach(inputId => {
          const fileInput = document.getElementById(inputId);
          if (fileInput) fileInput.value = "";
        });
        if (recaptchaRef) recaptchaRef.reset();
        setSubmitting(false);
        setCurrentStep(0);
      }
    } catch (error) {
      const errorInfo = handleUploadError(error, toast);
      setUploadError(errorInfo.message);

      if (errorInfo.type !== 'file_size') {
        handleSubmissionError(error, setErrors, toast, t);
      }
    } finally {
      setIsSubmitting(false);
      setUploadProgress(0);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccess(false);
    setCurrentStep(0);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderStepContent = (step, values, handleSendOTP) => {
    switch (step.id) {
      case "reporter":
        return (
          <div className="space-y-6">
            <FormField
              name="full_name"
              label={t("incident report.malware.fields.full_name")}
              placeholder={t("incident report.malware.fields.full_name_placeholder")}
            />
            <FormField
              name="email"
              label={t("incident report.malware.fields.email")}
              type="email"
              placeholder={t("incident report.malware.fields.email_placeholder")}
            />

            <FormField
              name="phone_number"
              label={t("incident report.malware.fields.phone_number")}
              placeholder={t("incident report.malware.fields.phone_number_placeholder")}
            />
            <FormField
              name="organization"
              label={t("incident report.malware.fields.organization")}
              placeholder={t("incident report.malware.fields.organization_placeholder")}
              required
            />
            <FormField
              name="reporter_type"
              label={t("incident report.malware.fields.reporter_type")}
              as="select"
              options={reporterTypes}
              required
            />
          </div>
        );
      case "malware":
        return (
          <div className="space-y-6">
            <FormField
              name="malware_type"
              label={t("incident report.malware.fields.malware_type")}
              as="select"
              options={malwareTypes}
            />
            {values.malware_type === "other" && (
              <FormField
                name="other_malware_type"
                label={t("incident report.malware.fields.other_malware_type")}
                placeholder={t("incident report.malware.fields.other_malware_type_placeholder")}
                required
              />
            )}
            <FormField
              name="detection_method"
              label={t("incident report.malware.fields.detection_method")}
              as="select"
              options={detectionMethods}
              required
            />
            {values.detection_method === "other" && (
              <FormField
                name="other_detection_method"
                label={t("incident report.malware.fields.other_detection_method")}
                placeholder={t("incident report.malware.fields.other_detection_method_placeholder")}
                required
              />
            )}
            <FormField
              name="affected_platform"
              label={t("incident report.malware.fields.affected_platform")}
              as="select"
              options={platformTypes}
              required
            />
            <FormField
              name="first_detection_date"
              label={t("incident report.malware.fields.first_detection_date")}
              type="date"
              required
            />
            <FormField
              name="infection_vector"
              label={t("incident report.malware.fields.infection_vector")}
              as="select"
              options={infectionVectors}
              required
            />
            {values.infection_vector === "other" && (
              <FormField
                name="other_infection_vector"
                label={t("incident report.malware.fields.other_infection_vector")}
                placeholder={t("incident report.malware.fields.other_infection_vector_placeholder")}
                required
              />
            )}
          </div>
        );
      case "analysis":
        return (
          <div className="space-y-6">
            <FormField
              name="observed_behavior"
              label={t("incident report.malware.fields.observed_behavior")}
              as="textarea"
              placeholder={t("incident report.malware.fields.observed_behavior_placeholder")}
            />
            <FormField
              name="impact_assessment"
              label={t("incident report.malware.fields.impact_assessment")}
              as="textarea"
              placeholder={t("incident report.malware.fields.impact_assessment_placeholder")}
            />

            {/* Archive password for password-protected ZIP samples */}
            <div className="mt-4">
              <FormField
                name="has_archive_password"
                type="checkbox"
                options={[{ name: t("incident report.malware.fields.password_protected") || "Password-protected archive" }]}
                label={t("incident report.malware.fields.password_protected") || "Password-protected archive"}
              />

              {values.has_archive_password && (
                <FormField
                  name="archive_password"
                  type="password"
                  label={t("incident report.malware.fields.archive_password") || "Archive password"}
                  placeholder={t("incident report.malware.fields.archive_password_placeholder") || "Enter archive password"}
                  required
                />
              )}
            </div>

            {/* Security Warning */}
            <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
              <div className="flex">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-400 mr-3 mt-1" />
                <div>
                  <p className="font-semibold text-red-800 mb-1">{t("incident report.malware.security_notice.title")}</p>
                  <p className="text-red-700 text-sm">
                    {t("incident report.malware.security_notice.text")}
                  </p>
                </div>
              </div>
            </div>

            <FormField
              name="malware_sample"
              label={t("incident report.malware.fields.malware_sample")}
              type="file"
              multiple
              required
              className="file:bg-red-50 file:border-red-200 file:text-red-700"
            />
            <FileSizeHelper fileType="malware_sample" />

            <FormField
              name="additional_files"
              label={t("incident report.malware.fields.additional_files")}
              type="file"
              multiple
            />
            <FileSizeHelper fileType="additional_files" />
          </div>
        );
      case "verify":
        return (
          <div className="space-y-6">
            <p className="text-gray-600">
              Click the button below to send an OTP to your email address for verification.
            </p>
            <div className="col-span-1 md:col-span-2">
              <button
                type="button"
                onClick={() => handleSendOTP(values.email)}
                disabled={!values.email}
                className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
              >
                Send OTP
              </button>
            </div>
            <FormField
              name="email_otp"
              label="OTP"
              placeholder="Enter the 6-digit OTP"
              required
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {showSuccess && (
        <SuccessNotification
          email={submittedEmail}
          trackingId={submittedTrackingId}
          onClose={handleCloseSuccess}
        />
      )}
      <PageHeader />
      <GradientBanner
        title={t("incident report.report_types.malware")}
        description={t("reportLanding.reportTypes.malware.description")}
        path={[
          { label: t("header.home"), link: "/" },
          { label: t("header.report_incident"), link: "/incident-report" },
          { label: t("incident report.report_types.malware"), link: null },
        ]}
        backgroundIcons={[faBug, faUser, faShieldAlt, faClipboardList]}
      />
      <div className="min-h-screen bg-gray-50 py-8 font-nunito-sans">
        <div className="px-dynamic">
          <Formik
            initialValues={initialValues}
            validationSchema={malwareValidationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, setFieldValue, validateForm, setTouched }) => {
              // Dynamically determine steps based on email
              const activeSteps = values.email ? steps : steps.filter(s => s.id !== 'verify');
              
              // Ensure currentStep is within bounds if steps change
              if (currentStep >= activeSteps.length) {
                setCurrentStep(activeSteps.length - 1);
              }

              const handleSendOTP = async (email) => {
                try {
                  await secureApi.post('/incident-reports/malware/send-otp/', { email });
                  setFieldValue("email_for_otp", email);
                  toast.success("OTP sent to your email address.");
                } catch (error) {
                  toast.error("Failed to send OTP. Please try again.");
                }
              };

              const currentStepObj = activeSteps[currentStep];

              return (
                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Left Sidebar - Steps */}
                  <div className="hidden lg:block lg:w-80 flex-shrink-0">
                    <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-4 max-h-screen overflow-visible">
                      <h2 className="text-xl font-bold text-gray-800 mb-6">{t("incident report.ui.report_progress")}</h2>
                      <div className="space-y-4">
                        {activeSteps.map((step, index) => (
                          <motion.div
                            key={step.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            className={`flex items-center p-4 rounded-lg transition-all cursor-pointer ${index === currentStep
                              ? "bg-primary text-white shadow-md"
                              : index < currentStep
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-gray-50 text-gray-500"
                              }`}
                            onClick={() => setCurrentStep(index)}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${index === currentStep
                              ? "bg-white/20"
                              : index < currentStep
                                ? "bg-green-100"
                                : "bg-gray-200"
                              }`}>
                              <FontAwesomeIcon
                                icon={index < currentStep ? faCheck : step.icon}
                                className={`${index === currentStep
                                  ? "text-white"
                                  : index < currentStep
                                    ? "text-green-600"
                                    : "text-gray-400"
                                  }`}
                              />
                            </div>
                            <div>
                              <div className="font-medium">{step.title}</div>
                              <div className={`text-sm ${index === currentStep
                                ? "text-white/80"
                                : index < currentStep
                                  ? "text-green-600"
                                  : "text-gray-400"
                                }`}>
                                {t("incident report.ui.step_of", { current: index + 1, total: activeSteps.length })}
                              </div>
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Right Content - Form */}
                  <div className="flex-1">
                    <Form>
                      <ScrollToError />
                      <motion.div
                        key={currentStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="bg-white rounded-2xl shadow-lg p-8"
                      >
                        <div className="mb-8">
                          <h1 className="text-3xl font-bold text-gray-800 mb-2">
                            {currentStepObj.title}
                          </h1>
                          <p className="text-gray-600">
                            {t("incident report.ui.step_of", { current: currentStep + 1, total: activeSteps.length })} - {t("incident report.ui.complete_form")}
                          </p>
                        </div>

                        {renderStepContent(currentStepObj, values, handleSendOTP)}

                        {/* Upload Progress */}
                        <UploadProgress
                          isUploading={isSubmitting && uploadProgress > 0}
                          progress={uploadProgress}
                          error={uploadError}
                          success={showSuccess}
                        />

                        {/* reCAPTCHA - Show only on last step */}
                        {currentStep === activeSteps.length - 1 && (
                          <div className="mt-6">
                            <ReCaptcha
                              ref={(ref) => setRecaptchaRef(ref)}
                              onChange={(token) => setFieldValue('recaptcha', token)}
                              onExpired={() => setFieldValue('recaptcha', '')}
                            />
                          </div>
                        )}

                        {/* Navigation Buttons */}
                        <div className="flex justify-between gap-3 mt-8 pt-6 border-t border-gray-200">
                          <button
                            type="button"
                            onClick={handlePrevious}
                            disabled={currentStep === 0}
                            className={`flex flex-1 sm:flex-none justify-center sm:justify-start items-center px-6 py-3 rounded-lg font-medium transition-all ${currentStep === 0
                              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                              : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                              }`}
                          >
                            <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                            {t("incident report.ui.previous")}
                          </button>

                          {currentStep === activeSteps.length - 1 ? (
                            <button
                              type="submit"
                              disabled={isSubmitting}
                              className="flex flex-1 sm:flex-none justify-center sm:justify-start items-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isSubmitting ? (
                                <>
                                  <InlineSpinner size="sm" className="mr-2 border-white/30 border-t-white" />
                                  {t("incident report.ui.submitting")}
                                </>
                              ) : (
                                <>
                                  Submit Report
                                  <FontAwesomeIcon icon={faCheck} className="ml-2" />
                                </>
                              )}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleNext(validateForm, values, setTouched, activeSteps)}
                              className="flex flex-1 sm:flex-none justify-center sm:justify-start items-center px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-all"
                            >
                              Next
                              <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    </Form>
                  </div>
                </div>
              )
            }}
          </Formik>
        </div>
      </div>
      <Toaster position="top-right" />
    </>
  );
};

export default SteppedMalwareForm;
